import React from 'react';
import PropTypes from 'prop-types';
import kebabCase from 'lodash/kebabCase';
import SliceErrorBoundary from '../SliceErrorBoundary';
import cx from 'classnames';
import get from 'lodash/get';

import './DynamicLayout.scss';

// Components
import Hero from '../Hero';
import HeroII from '../HeroII';
import HeroIII from '../HeroIII';
import HeroIV from '../HeroIV';
import CallToAction from '../CallToAction';
import CrossSell from '../CrossSell';
import PromoBannerInline from './PromoBannerInline';
import ReferAFriend from '../ReferAFriend';
import ValueProps from '../ValueProps';
import ValuePropsII from '../ValuePropsII';
import FeaturedItems from '../FeaturedItems';
import VideoBackgroundSlider from '../VideoBackgroundSlider';
import LogoIconCollection from '../LogoIconCollection';
import BlogArticles from '../BlogArticles';
import Newsletter from '../Newsletter';
import TextOverBackgroundImage from '../TextOverBackgroundImage';
import ProductGrid from '../ProductGrid';
import ProductList from '../ProductList';
import ProductListII from '../ProductListII';
import ProductHero from '../ProductHero';
import ProductForm from '../ProductForm';
import ProductInformation from '../ProductInformation';
import FullWidthImage from '../FullWidthImage';
import ProductReviewList from '../ProductReviewList';
import RelatedProducts from '../RelatedProducts';
import Canvas from '../Canvas';
import AddOns from '../AddOns';
import CartItems from '../CartItems';
import CartItemsII from '../CartItemsII';
import Upsell from '../Upsell';
import CartShipping from '../Shipping';
import CartSummary from '../CartSummary';
import Description from '../Description';
import ChooseYourCountry from '../ChooseYourCountry';
import UserForm from '../UserForm';
import UserOrders from '../UserOrders';
import AddToCart from '../AddToCart';
import StepBannerImage from '../StepBannerImage';
import TrustpilotWidget from '../TrustpilotWidget';
import SkipTheQueue from '../SkipTheQueue';
import Features from '../Features';
import HelpContentModules from '../HelpContentModules';
import CheckoutOrderSummary from '../MainCheckout/CheckoutOrderSummary';
import VerifyPayment from '../VerifyPayment';
import SignInPartial from '../SignInPartial';
import ContactAndShipping from '../ContactAndShipping';
import PaymentMethods from '../PaymentMethods';
import IntroPartial from '../IntroPartial';
import Group from '../Group';
import Column from '../Column';
import OffersCarousel from '../OffersCarousel';
import OffersList from '../OffersList';
import WelcomePartial from '../Account/WelcomePartial';
import OrderConfirmationSummary from '../OrderConfirmationSummary';
import DynamicLoginPartial from '../DynamicLoginPartial';
import CreditInfo from '../Account/CreditInfo';
import AmazonCheckout from '../PaymentMethods/Amazon';

const components = {
  Hero,
  HeroII,
  HeroIII,
  HeroIV,
  CallToAction,
  CrossSell,
  PromoBannerInline,
  ReferAFriend,
  ValueProps,
  ValuePropsII,
  FeaturedItems,
  VideoBackgroundSlider,
  Canvas,
  LogoIconCollection,
  BlogArticles,
  Newsletter,
  TextOverBackgroundImage,
  ProductGrid,
  ProductList,
  ProductListII,
  ProductHero,
  ProductForm,
  ProductInformation,
  FullWidthImage,
  ProductReviewList,
  RelatedProducts,
  AddOns,
  CartItems,
  CartItemsII,
  Upsell,
  CartShipping,
  CartSummary,
  Description,
  ChooseYourCountry,
  UserForm,
  UserOrders,
  AddToCart,
  StepBannerImage,
  TrustpilotWidget,
  SkipTheQueue,
  Features,
  HelpContentModules,
  CheckoutOrderSummary,
  VerifyPayment,
  SignInPartial,
  ContactAndShipping,
  PaymentMethods,
  IntroPartial,
  Group,
  Column,
  OffersCarousel,
  OffersList,
  WelcomePartial,
  OrderConfirmationSummary,
  DynamicLoginPartial,
  CreditInfo,
  AmazonCheckout
};

const getComponent = (slice, i) => {
  let type = slice.type;
  let items = (slice.items || []).slice();

  const Component = components[type];
  if (!Component) {
    return;
  }

  if (['Group', 'Column'].includes(type)) {
    items = slice.items.map(getComponent);
  }

  return (
    <SliceErrorBoundary key={`${slice.type}_${i}`}>
      <Component {...slice} items={items} />
    </SliceErrorBoundary>
  );
};

const getSlice = (sliceNames, slices) => {
  const index = slices.findIndex(slice => sliceNames.includes(slice.type));
  if (index === -1) {
    return {
      index,
      slice: {},
    };
  }
  return {
    index,
    slice: slices.splice(index, 1)[0]
  };
};

const DynamicLayout = props => {
  const page = get(props, 'model.prismicPageModel', {});

  // shallow cloning as to make changes to the array without effecting the original array
  const slices = (page.slices || []).slice();
  const layout = page.layout || '';

  const isTwoColumnLayout = !!page.isTwoColumnLayout;
  const isTwoColumnLayoutActive = (props.pageId === 'cart' || props.isCheckout) && isTwoColumnLayout;

  // Setting initial values
  let cartSummaryIndex = -1;
  let cartSummarySlice = {};
  let helpContentModulesIndex = -1;
  let helpContentModulesSlice = {};

  if (isTwoColumnLayoutActive) {
    const cartSummary = getSlice(['CartSummary', 'CheckoutOrderSummary'], slices);
    cartSummarySlice = cartSummary.slice || cartSummarySlice;
    cartSummaryIndex = cartSummary.index || cartSummaryIndex;

    // Hack to expand help content modules full width without overlapping the content
    const helpContentModules = getSlice(['HelpContentModules'], slices);
    helpContentModulesIndex = helpContentModules.index || helpContentModulesIndex;
    helpContentModulesSlice = helpContentModules.slice || helpContentModulesSlice;
  }

  const banners = get(props, 'model.prismicGlobalContentModel.general.banners', []);
  const isNewProductAdded = props.location.search && props.location.search.includes('product-added');
  const doesProductAddedBannerExist = banners.some(banner => banner.banner.slug === 'product-added-banner');
  return (
    <main
      className={cx('wrapper', `${kebabCase(layout)}`, {
        'two-column-layout': isTwoColumnLayoutActive,
        'product-added': isNewProductAdded && doesProductAddedBannerExist,
        'wrapper--checkout': props.isCheckout,
      })}
      id={props.pageId}
      data-test={props.pageId}
      style={{
        backgroundColor: page.backgroundColor,
      }}>
      {!isTwoColumnLayoutActive &&
        slices.map((slice, i) => getComponent(slice, i))
      }
      {isTwoColumnLayoutActive &&
        <React.Fragment>
          <div className="left-column">
            {slices.map((slice, i) => getComponent(slice, i))}
          </div>
          <div className="right-column">
            {getComponent(cartSummarySlice, cartSummaryIndex)}
          </div>
          {helpContentModulesIndex !== -1 &&
            <div className="full-width-column">
              {getComponent(helpContentModulesSlice, helpContentModulesIndex)}
            </div>
          }
        </React.Fragment>
      }
    </main>
  );
};

DynamicLayout.propTypes = {
  pageId: PropTypes.string.isRequired,
  model: PropTypes.shape({
    prismicPageModel: PropTypes.shape({
      isTwoColumnLayout: PropTypes.bool,
      slices: PropTypes.array,
      layout: PropTypes.string
    }),
    prismicGlobalContentModel: PropTypes.shape({
      general: PropTypes.shape({
        banners: PropTypes.array
      })
    })
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  isCheckout: PropTypes.bool,
};

export default DynamicLayout;



// WEBPACK FOOTER //
// ./src/client/js/view/components/DynamicLayout/DynamicLayout.js